import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, Text, Button, VStack, useColorModeValue, Heading, HStack, Circle } from '@chakra-ui/react';
import EquipmentContent from './EquipmentContent';
import { getAdminUserDetail, getUnreadItemTypesByUserId } from '../services/api';

const AdminUserDetail = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadItemTypes, setUnreadItemTypes] = useState([]);
  const { userId } = useParams();
  const { currentUser, isFunctionEnabled } = useAuth();
  const navigate = useNavigate();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userData = await getAdminUserDetail(userId);
        setUser(userData);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('ユーザー情報の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId, currentUser]);

  // 未読データを取得
  useEffect(() => {
    const fetchUnreadData = async () => {
      if (!currentUser || !isFunctionEnabled('chat')) {
        setUnreadItemTypes([]);
        return;
      }

      try {
        const unreadData = await getUnreadItemTypesByUserId(userId);
        setUnreadItemTypes(unreadData.item_types);
      } catch (error) {
        console.error('未読データの取得に失敗:', error);
        setUnreadItemTypes([]);
      }
    };

    fetchUnreadData();
  }, [currentUser, userId, isFunctionEnabled]);

  if (loading) return <Text>読み込み中...</Text>;
  if (error) return <Text color="red.500">{error}</Text>;
  if (!user) return <Text>ユーザーが見つかりません</Text>;

  const displayName = user.name ? `${user.name} 様邸` : `${user.email} 様邸`;

  return (
    <Box>
      <HStack
        spacing={4}
        align="stretch"
        bg={bgColor}
        borderColor={borderColor}
        borderWidth={1}
        borderRadius="md"
        p={4}
        mb={4}
      >
        <Button
          variant="outline"
          onClick={() => navigate('/admin/users')}
          alignSelf="flex-start"
        >
          ←顧客一覧
        </Button>
        <Text fontSize="xl" fontWeight="bold" flex={1}>
          {displayName}
        </Text>
        <HStack spacing={2}>
          {/* チャット機能が有効な場合のみ表示 */}
          {isFunctionEnabled('chat') && (
            <Button
              as={RouterLink}
              to={`/admin/user/${userId}/chat`}
              size="sm"
              bg="gray.50"
              _hover={{ color: 'yellow.700' }}
              _active={{ color: 'yellow.700' }}
              position="relative"
            >
              ご相談
              {unreadItemTypes.includes("anything") && (
                <Circle
                  size="8px"
                  bg="yellow.700"
                  position="absolute"
                  top="-2px"
                  right="-2px"
                />
              )}
            </Button>
          )}
          <Button
            as={RouterLink}
            to={`/admin/user/${userId}/like-images`}
            size="sm"
            bg="gray.50"
            _hover={{ color: 'yellow.700' }}
            _active={{ color: 'yellow.700' }}
            position="relative"
          >
            お気に入り画像
            {unreadItemTypes.includes("like_images") && (
              <Circle
                size="8px"
                bg="yellow.700"
                position="absolute"
                top="-2px"
                right="-2px"
              />
            )}
          </Button>
        </HStack>
      </HStack>

      <EquipmentContent targetUserId={userId} />
    </Box>
  );
};

export default AdminUserDetail; 